import {
  AssetFilterParams,
  AssetHistoryQueryParams,
  AssetLocationUpdatedEvent,
  AssetStatusUpdatedEvent,
  CreateAssetDto,
  RealtimeEventType,
  UpdateAssetDto,
} from '@peritasai/types';
import { useMemo } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useRealtimeAssetUpdates } from './realtime-hooks';
import { assetRepository } from './repository';

/**
 * Query keys for asset queries
 */
export const assetKeys = {
  all: ['assets'] as const,
  lists: () => [...assetKeys.all, 'list'] as const,
  list: (filters: AssetFilterParams) =>
    [...assetKeys.lists(), { filters }] as const,
  item: (id: string) => [...assetKeys.all, 'item', id] as const,
  history: (assetId: string, params: AssetHistoryQueryParams) =>
    [...assetKeys.all, 'history', assetId, params] as const,
} as const;

/**
 * Fetches multiple assets by id in parallel (there is no batch-by-ids
 * endpoint). Used to look up the physical reference number for assets
 * already attached to a checklist item (e.g. the Build Cart "On Cart"
 * column), where the checklist only carries the assetId.
 * Also subscribes to realtime asset status/location updates (unfiltered by
 * location, so assets leaving a location are caught too) and invalidates the
 * matching asset's cache entry, so location/status reflect changes live
 * instead of waiting out the 5-minute staleTime.
 */
export function useAssetsByIds(ids: string[]) {
  const queryClient = useQueryClient();

  useRealtimeAssetUpdates({
    eventTypes: [
      RealtimeEventType.ASSET_STATUS_UPDATED,
      RealtimeEventType.ASSET_LOCATION_UPDATED,
    ],
    enabled: ids.length > 0,
    onEvent: (event) => {
      if (
        event.type !== RealtimeEventType.ASSET_STATUS_UPDATED &&
        event.type !== RealtimeEventType.ASSET_LOCATION_UPDATED
      ) {
        return;
      }
      const assetId = event.data.asset.id;
      if (!ids.includes(assetId)) return;
      void queryClient.invalidateQueries({ queryKey: assetKeys.item(assetId) });
    },
  });

  return useQueries({
    queries: ids.map((id) => ({
      queryKey: assetKeys.item(id),
      queryFn: () => assetRepository.getAssetById(id),
      staleTime: 5 * 60 * 1000,
      enabled: !!id,
    })),
  });
}

// Unlike AssetCatalogItemEntity.bestAvailableLocation (AVAILABLE-status units
// only), this surfaces every unit's real status.
export function useAssetsByCatalogIds(catalogItemIds: string[]) {
  const queryClient = useQueryClient();

  useRealtimeAssetUpdates({
    eventTypes: [
      RealtimeEventType.ASSET_STATUS_UPDATED,
      RealtimeEventType.ASSET_LOCATION_UPDATED,
    ],
    onEvent: (event) => {
      if (
        event.type !== RealtimeEventType.ASSET_STATUS_UPDATED &&
        event.type !== RealtimeEventType.ASSET_LOCATION_UPDATED
      ) {
        return;
      }
      const { assetCatalogId } = (
        event as AssetStatusUpdatedEvent | AssetLocationUpdatedEvent
      ).data.asset;
      void queryClient.invalidateQueries({
        queryKey: assetKeys.list({ assetCatalogId }),
      });
    },
  });

  return useQueries({
    queries: catalogItemIds.map((catalogItemId) => ({
      queryKey: assetKeys.list({ assetCatalogId: catalogItemId }),
      queryFn: () =>
        assetRepository.getAssets({ assetCatalogId: catalogItemId, pageSize: 100 }),
      staleTime: 5 * 60 * 1000,
      enabled: !!catalogItemId,
    })),
  });
}

/**
 * Hook for fetching assets with optional filters
 * Fetches all assets (client-side pagination)
 */
export function useAssets(filters: AssetFilterParams = {}) {
  const queryKey = assetKeys.list(filters);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      return assetRepository.getAssets(filters);
    },
    staleTime: 30 * 1000, // 30 seconds
  });

  return query;
}

/**
 * Hook for fetching assets with server-side pagination
 */
export function useAssetsPaginated(
  filters: AssetFilterParams = {},
  page = 0,
  pageSize = 20,
  enabled = true
) {
  const queryKey = [...assetKeys.list(filters), 'paginated', page, pageSize];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      return assetRepository.getAssetsPaginated({
        ...filters,
        page,
        pageSize,
      });
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000, // 30 seconds
  });

  return query;
}

/**
 * Hook that fetches every page for a given filter set and merges the results.
 * Returns the same data shape as useAssetsPaginated so callers need no
 * structural changes. Each page is independently cached by React Query.
 */
export function useAssetsPaginatedAll(
  filters: AssetFilterParams = {},
  pageSize = 100,
  enabled = true
) {
  const firstPage = useAssetsPaginated(filters, 0, pageSize, enabled);

  const total = firstPage.data?.total ?? 0;
  const additionalPageCount = total > 0 ? Math.max(0, Math.ceil(total / pageSize) - 1) : 0;

  const additionalPages = useQueries({
    queries: Array.from({ length: additionalPageCount }, (_, i) => ({
      queryKey: [...assetKeys.list(filters), 'paginated', i + 1, pageSize],
      queryFn: () =>
        assetRepository.getAssetsPaginated({ ...filters, page: i + 1, pageSize }),
      enabled: enabled && firstPage.isSuccess,
      staleTime: 30 * 1000,
      placeholderData: keepPreviousData,
    })),
  });

  // useQueries returns a new array reference every render, so depending on
  // additionalPages directly would recompute on every render even when data
  // hasn't changed. dataUpdatedAt is a stable numeric timestamp that React
  // Query updates only when new data arrives, breaking the reference chain.
  const additionalAssets = useMemo(
    () => additionalPages.flatMap((q) => q.data?.assets ?? []),
    [additionalPages.map((q) => q.dataUpdatedAt ?? 0).join(',')]
  );

  const data = useMemo(() => {
    if (!firstPage.data) return undefined;
    return {
      ...firstPage.data,
      assets: [
        ...(firstPage.data.assets ?? []),
        ...additionalAssets,
      ],
    };
  }, [firstPage.data, additionalAssets]);

  return {
    data,
    isLoading: firstPage.isLoading || additionalPages.some((q) => q.isLoading),
    isError: firstPage.isError || additionalPages.some((q) => q.isError),
    error: firstPage.error ?? additionalPages.find((q) => q.error)?.error ?? null,
    isFetched: firstPage.isFetched && additionalPages.every((q) => q.isFetched || q.isError),
    isSuccess: firstPage.isSuccess && additionalPages.every((q) => q.isSuccess || !q.isPending),
    refetch: () => Promise.all([
      firstPage.refetch(),
      ...additionalPages.map((q) => q.refetch()),
    ]),
  };
}

/**
 * Hook for fetching asset history with server-side pagination
 */
export function useAssetHistoryPaginated(
  assetId: string | null | undefined,
  params: AssetHistoryQueryParams,
  enabled = true
) {
  const queryKey = assetId ? assetKeys.history(assetId, params) : [];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!assetId) {
        throw new Error('assetId is required to fetch history');
      }
      return assetRepository.getAssetHistory(assetId, params);
    },
    enabled: Boolean(assetId) && enabled,
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  return query;
}

export function useCreateAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateAssetDto) => assetRepository.createAsset(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  });
}

export function useUpdateAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateAssetDto }) =>
      assetRepository.updateAsset(id, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assetKeys.lists() });
    },
  });
}
